import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { listOrders } from '@/api/orders';
import { Receipt, Banknote } from 'lucide-react';

export function PaymentFlowPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['orders', 'paid'],
    queryFn: () => listOrders({ status: 'paid' }),
  });

  const totalAmount = data?.items.reduce((sum, o) => sum + parseFloat(o.totalAmount || '0'), 0) ?? 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-[#1E293B]">收款流水</h1>
        <p className="mt-1 text-sm text-[#64748B]">
          查看已收款订单的支付记录
        </p>
      </div>

      {/* 统计 */}
      <Card className="mb-4">
        <CardContent className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-[#64748B]" />
            <span className="text-sm text-[#64748B]">
              共 <span className="font-medium text-[#1E293B]">{data?.total ?? 0}</span> 笔已收款
            </span>
          </div>
          <div className="text-sm text-[#64748B]">
            合计金额 <span className="font-semibold text-[#16A34A]">¥{totalAmount.toFixed(2)}</span>
          </div>
        </CardContent>
      </Card>

      {/* 收款记录 */}
      <Card>
        <CardHeader>
          <CardTitle>收款记录</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-12 text-center text-sm text-[#94A3B8]">加载中...</div>
          ) : isError ? (
            <div className="py-12 text-center text-sm text-[#DC2626]">加载失败，请刷新重试</div>
          ) : !data?.items.length ? (
            <div className="py-12 text-center">
              <Receipt className="mx-auto h-12 w-12 text-[#E2E8F0]" />
              <p className="mt-3 text-sm text-[#94A3B8]">
                暂无收款记录
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#E2E8F0] text-left text-sm text-[#64748B]">
                    <th className="pb-3 font-medium">订单号</th>
                    <th className="pb-3 font-medium">客户/车辆</th>
                    <th className="pb-3 font-medium">收款方式</th>
                    <th className="pb-3 font-medium">金额</th>
                    <th className="pb-3 font-medium">收款时间</th>
                    <th className="pb-3 font-medium">状态</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F1F5F9]">
                  {data.items.map((order) => (
                    <tr key={order.id} className="text-sm">
                      <td className="py-3">
                        <span className="font-mono text-xs text-[#64748B]">
                          {order.id.slice(0, 8)}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="font-medium text-[#1E293B]">{order.customerName}</div>
                        <div className="mt-0.5 text-xs text-[#94A3B8]">{order.vehiclePlate}</div>
                      </td>
                      <td className="py-3">
                        <span className="flex items-center gap-1.5 text-[#64748B]">
                          <Banknote className="h-4 w-4" />
                          线下收款
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="font-medium text-[#16A34A]">¥{order.totalAmount}</span>
                      </td>
                      <td className="py-3 text-[#94A3B8]">
                        {order.createdAt
                          ? new Date(order.createdAt).toLocaleString('zh-CN')
                          : '-'}
                      </td>
                      <td className="py-3">
                        <Badge variant="success">已收款</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
